import React from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  ButtonBase,
  IconButton,
  Skeleton,
  Chip,
} from '@mui/material';
import { Trash2, ChevronLeft, ChevronRight, Inbox } from 'lucide-react';
import { BankSearch } from '../../../../utils/types/candi_bank_types';
import {
  formatRelativeTime,
  sourceLabel,
} from '../../utils/bankSearchAdapters';

interface BankSearchListProps {
  searches: BankSearch[];
  total: number;
  page: number;
  loading: boolean;
  activeSearchId: string | null;
  onSelect: (search: BankSearch) => void;
  onDelete: (searchId: string) => void;
  onPageChange: (page: number) => void;
}

export const BankSearchList: React.FC<BankSearchListProps> = ({
  searches,
  total,
  page,
  loading,
  activeSearchId,
  onSelect,
  onDelete,
  onPageChange,
}) => {
  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this saved search?')) {
      onDelete(id);
    }
  };

  const totalPages = Math.ceil(total / 20);

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: 'background.paper',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          overflow: 'hidden',
        }}
      >
        <Stack spacing={1} sx={{ p: 1.5, flex: 1, overflowY: 'auto' }}>
          {[...Array(5)].map((_, i) => (
            <Paper
              key={i}
              elevation={0}
              sx={{
                p: 1.5,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.100',
              }}
            >
              <Skeleton variant="text" width="75%" height={20} />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Skeleton variant="rounded" width={56} height={18} />
                <Skeleton variant="rounded" width={56} height={18} />
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'grey.200',
        overflow: 'hidden',
        minHeight: 500,
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
        {searches.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: 200, color: 'text.disabled' }}
            spacing={1}
          >
            <Inbox size={32} opacity={0.5} />
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              No saved searches yet
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1}>
            {searches.map((search) => {
              const isActive = search.id === activeSearchId;
              return (
                <ButtonBase
                  key={search.id}
                  onClick={() => onSelect(search)}
                  sx={{
                    width: '100%',
                    textAlign: 'left',
                    display: 'block',
                    p: 1.5,
                    borderRadius: 2,
                    position: 'relative',
                    transition: 'all 0.2s',
                    border: '1px solid',
                    borderColor: isActive ? 'primary.light' : 'grey.200',
                    bgcolor: isActive ? 'primary.50' : 'background.paper',
                    '&:hover': {
                      bgcolor: isActive ? 'primary.50' : 'grey.50',
                      borderColor: isActive ? 'primary.main' : 'grey.300',
                    },
                    '&:hover .delete-btn': {
                      opacity: 1,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: isActive ? 'primary.dark' : 'text.primary',
                        pr: 3,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {search.title || 'Untitled Search'}
                    </Typography>
                    <IconButton
                      size="small"
                      className="delete-btn"
                      onClick={(e) => handleDelete(e, search.id)}
                      title="Delete Search"
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        opacity: 0,
                        transition: 'opacity 0.15s',
                        p: 0.5,
                        color: 'text.secondary',
                        '&:hover': { color: 'error.main', bgcolor: 'error.50' },
                      }}
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </Box>

                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                    <Chip
                      label={sourceLabel(search.source)}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.625rem', fontWeight: 600 }}
                    />
                    <Chip
                      label={search.status}
                      size="small"
                      variant="outlined"
                      color={search.status === 'Completed' ? 'success' : search.status === 'Error' ? 'error' : 'default'}
                      sx={{ height: 20, fontSize: '0.625rem', fontWeight: 600 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        ml: 'auto !important',
                        fontSize: '0.625rem',
                        fontWeight: 500,
                      }}
                    >
                      {formatRelativeTime(search.updatedAt)}
                    </Typography>
                  </Stack>
                </ButtonBase>
              );
            })}
          </Stack>
        )}
      </Box>

      {total > 0 && (
        <Box
          sx={{
            p: 1.25,
            borderTop: '1px solid',
            borderColor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'grey.50',
          }}
        >
          <IconButton
            size="small"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft size={16} />
          </IconButton>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            Page {page} {totalPages > 0 && `of ${totalPages}`}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
          >
            <ChevronRight size={16} />
          </IconButton>
        </Box>
      )}
    </Paper>
  );
};
